
import React, { useState, useEffect, useRef } from 'react';
import ModalWrapper from '../ModalWrapper';
import { TextField, RadioGroup, FormControlLabel, Radio, Typography, Box } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { resolveModelForProviderModel } from '../../utils/api/aiFeatureModel_apis';
import { fetchEnvironmentList } from '../../utils/api/environment_apis';
import { getTrialUsage } from '../../utils/api/trialUsage_apis';
import { getTestGroupSummary } from '../../utils/api/testgroup_apis';
import { createTestLaunch } from '../../utils/api/testlaunch_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { MIN_CREDITS_FOR_LAUNCH } from '../../constants/trialLimits';
import { useSnackbar } from '../../utils/useSnackbar';
import { useSelector } from 'react-redux';
import { FormSchemas, FormWrapper, CommonFooter } from '../FormComponent';
import { AI_FEATURE_KEYS } from '../../enums/AIModelEnums';
import { userCanSelectAiModel } from '../../utils/commonfunctions';
import AIModelRuntimeSelector from '../Select/llmModelSelector/AIModelRuntimeSelector';

const users = [
  { value: '', label: 'Select a user' },
  // Populate with real users in integration
];

// Custom Start Time Component
const StartTimeField = ({ value, onChange, customOnChange, name, ...props }) => {
  // Use local state for the radio button value
  const [localValue, setLocalValue] = useState('Immediate');
  
  // Update local state when prop value changes
  useEffect(() => {
    if (value !== undefined) {
      setLocalValue(value);
    }
  }, [value]);
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    
    // Only allow 'Immediate' selection since 'Schedule' is disabled
    if (newValue === 'Immediate') {
      setLocalValue(newValue);
      
      // Call the custom onChange handler to update parent state
      if (customOnChange) {
        customOnChange(name, newValue);
      }
      
      // Also call the form onChange if provided
      if (onChange) {
        const syntheticEvent = {
          target: {
            name: name,
            value: newValue
          }
        };
        onChange(syntheticEvent);
      }
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
        Start Time
      </Typography>
      <RadioGroup 
        row 
        value={localValue} 
        onChange={handleChange}
        sx={{ ml: 1 }}
      >
        <FormControlLabel 
          value="Immediate" 
          control={<Radio />} 
          label="Immediate" 
        />
        <FormControlLabel 
          value="Schedule" 
          control={<Radio disabled={true} />} 
          label="Schedule" 
          sx={{ 
            '& .MuiFormControlLabel-label': { 
              color: 'text.disabled',
              cursor: 'not-allowed'
            },
            cursor: 'not-allowed'
          }}
        />
      </RadioGroup>
    </Box>
  );
};

// Custom Scheduled Time Picker Field
const ScheduledTimeField = (props) => {
  let value = props.value;
  if (typeof value === 'string' && value) {
    value = new Date(value);
  }
  if (value && !(value instanceof Date) && isNaN(value)) {
    value = null;
  }
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <DateTimePicker
        {...props}
        value={value || null}
        onChange={(val) => props.onChange(val)}
        format="dd/MM/yyyy hh:mm a"
        ampm={true}
        slotProps={{
          textField: { fullWidth: true, error: props.error, helperText: props.helperText },
          popper: { container: document.body },
        }}
      />
    </LocalizationProvider>
  );
};

function CreateTestLaunchModal({ open, onClose, onSubmit, group, projectId }) {
  const { user } = useSelector((state) => state.auth);
  const canSelectAiModel = userCanSelectAiModel(user, projectId);
  const isTrial = user?.plan_name === 'trial';
  const [startType, setStartType] = useState('Immediate');
  const [isSequentialEnabled, setIsSequentialEnabled] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isDynamic] = useState(false);
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEnvironments, setFetchingEnvironments] = useState(false);
  const [testScope, setTestScope] = useState({ total: 0, automated: 0, manual: 0 });
  const [fetchingTestScope, setFetchingTestScope] = useState(false);
  const [modalError, setModalError] = useState('');
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [trialExpired, setTrialExpired] = useState(false);
  const formRef = useRef(null);
  const { showSnackbar } = useSnackbar();

  // Required credits = MIN_CREDITS_FOR_LAUNCH × number of NLP automated test cases
  const automatedCount = testScope.automated ?? 0;
  const requiredCreditsForLaunch = MIN_CREDITS_FOR_LAUNCH * automatedCount;
  const canStartTestLaunch = !isTrial
    ? true
    : !trialExpired && (creditsRemaining ?? 0) >= requiredCreditsForLaunch;

  // Fetch trial usage when modal opens to gate Launch
  useEffect(() => {
    if (open && isTrial) {
      getTrialUsage()
        .then((res) => {
          if (res?.status === 'success' && res?.data) {
            setCreditsRemaining(res.data.credits_remaining != null ? Number(res.data.credits_remaining) : 0);
            setTrialExpired(Boolean(res.data.is_expired));
          } else {
            setCreditsRemaining(null);
            setTrialExpired(false);
          }
        })
        .catch(() => {
          setCreditsRemaining(null);
          setTrialExpired(false);
        });
    } else if (open && !isTrial) {
      setCreditsRemaining(null);
      setTrialExpired(false);
    }
  }, [open, isTrial]);

  // Reset startType when modal opens
  useEffect(() => {
    if (open) {
      setStartType('Immediate');
      setIsSequentialEnabled(false);
    }
  }, [open]);

  // Fetch environments, test scope, and AI models when modal opens
  useEffect(() => {
    if (open) {
      fetchEnvironments();
      if (group?.id) {
        fetchTestScope();
      }
    }
  }, [open, projectId, group?.id]);


  const handleFormSubmit = async () => {
    if (formRef.current) {
      try {
        const currentValues = formRef.current.getValues?.() || {};
        const hasAutomatedNlpTests = (testScope.automated || 0) > 0;
        if (hasAutomatedNlpTests && !currentValues.aiModel) {
          showSnackbar(
            'No AI model configured for Test Launch. Configure a model in Administration > Configuration.',
            'error'
          );
          return;
        }
        // Use the form's handleSubmit method which will handle validation properly
        await formRef.current.handleSubmit(handleConfirmSubmit)();
      } catch (error) {
        console.error('Form submit error:', error);
      }
    }
  };

  const handleConfirmSubmit = async (formValues) => {
    setModalError('');
    setLoading(true);
    
    try {
      // Check if test group is empty before proceeding
      if (testScope.total === 0) {
        showSnackbar('Cannot create test launch for an empty test group. Please add test cases to the group first.', 'error');
        setLoading(false);
        return;
      }

      // Check if test group exceeds the 100 test case limit
      if (testScope.total > 100) {
        showSnackbar(`A launch cannot have more than 100 test cases. Please reduce the group to 100 or fewer (currently ${testScope.total}).`, 'error');
        setLoading(false);
        return;
      }

      // Send structured model reference (provider/model_key) and explicit aiApiKeyId.
      let normalizedLlmModelId = formValues.aiModel;
      if (typeof normalizedLlmModelId === 'string' && normalizedLlmModelId.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(normalizedLlmModelId);
          if (parsed?.provider && parsed?.model_key) {
            normalizedLlmModelId = {
              provider: parsed.provider,
              model_key: parsed.model_key,
            };
          }
        } catch (e) {
          // Keep original value for backend validation/error handling.
        }
      }

      let aiApiKeyId = null;
      if (
        normalizedLlmModelId &&
        typeof normalizedLlmModelId === 'object' &&
        normalizedLlmModelId.provider &&
        normalizedLlmModelId.model_key
      ) {
        const resolveRes = await resolveModelForProviderModel(
          normalizedLlmModelId.provider,
          normalizedLlmModelId.model_key,
          projectId ?? group?.project_id
        );
        aiApiKeyId = resolveRes?.data?.data?.ai_api_key_id || null;
        if (aiApiKeyId) {
          normalizedLlmModelId = {
            ...normalizedLlmModelId,
            aiApiKeyId,
          };
        }
      }

      // Prepare the config object for environment and AI model
      const config = {
        ...(formValues.environment && { environmentId: formValues.environment }),
        ...(normalizedLlmModelId && { llmModelId: normalizedLlmModelId }),
        ...(formValues.isSequential && { continueOnFailure: formValues.continueOnFailure !== false }),
        ...(isDynamic && { isDynamic: true }),
      };

      const hostname = window.location.hostname;
      // Call the API to create test launch
      const response = await createTestLaunch({
        testgroupId: group?.id,
        name: formValues.launchName,
        isSequential: formValues.isSequential,
        config: config,
        hostname: hostname
      });
      
      if (response.status === STATUS.SUCCESS) {
        showSnackbar(response.message || `Test launch created successfully: ${formValues.launchName}`, 'success');
        window.dispatchEvent(new CustomEvent('trialUsageRefresh'));
        onSubmit && onSubmit({
          testgroupId: group?.id,
          name: formValues.launchName,
          isSequential: formValues.isSequential,
          config: config
        });
        onClose();
      } else {
        showSnackbar(response.error || 'Failed to create test launch', 'error');
      }
    } catch (error) {
      console.error('Error creating test launch:', error);
      showSnackbar(error.message || 'Failed to create test launch', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnvironments = async () => {
    setFetchingEnvironments(true);
    try {
      const response = await fetchEnvironmentList();
      if (response.status === STATUS.SUCCESS) {
        setEnvironments(response.data || []);
      } else {
        showSnackbar(response.error || 'Failed to fetch environments', 'error');
      }
    } catch (error) {
      console.error('Error fetching environments:', error);
      showSnackbar('Error fetching environments', 'error');
    } finally {
      setFetchingEnvironments(false);
    }
  };

  const fetchTestScope = async () => {
    if (!group?.id) return;
    
    setFetchingTestScope(true);
    try {
      const response = await getTestGroupSummary({ testGroupId: group.id });
      if (response.status === STATUS.SUCCESS) {
        // API returns data directly
        setTestScope(response.data || { total: 0, automated: 0, manual: 0 });
      } else {
        setTestScope({ total: 0, automated: 0, manual: 0 });
      }
    } catch (error) {
      console.error('Error fetching test scope:', error);
      showSnackbar('Error fetching test scope', 'error');
      // Set default values on error
      setTestScope({ total: 0, automated: 0, manual: 0 });
    } finally {
      setFetchingTestScope(false);
    }
  };

 const  getLaunchName = () => {
    const datePart = new Date().toISOString().slice(0, 10);
    const randomPart = String(Math.floor(Math.random() * 100000)).padStart(5, '0');
    const fixedLength = datePart.length + randomPart.length + 2; // 2 dashes
    const maxGroupLength = 100 - fixedLength; // 83 characters
    const groupName = group?.name || '';
    const trimmedGroupName = groupName.length > maxGroupLength 
      ? group.name.slice(0, maxGroupLength) 
      : group.name;
    return `${trimmedGroupName}-${datePart}-${randomPart}`;
   }
  // Field definitions for FormWrapper
  const fields = [
    {
      name: 'launchName',
      type: 'text',
      label: 'Launch Name',
      placeholder: group ? getLaunchName() : 'Enter launch name',
      required: true,
      autoFocus: true,
    },
    {
      name: 'testScope',
      type: 'text',
      label: 'Test Scope',
      value: !group?.id ? 'N/A' : fetchingTestScope ? 'Loading...' : testScope.total === 0 ? 'No test cases found - Cannot create launch' : `${testScope.automated || 0} automated : ${testScope.manual || 0} manual - ${testScope.total || 0} in total`,
      disabled: true,
      error: testScope.total === 0 || testScope.total > 100,
      helperText: testScope.total === 0 ? 'Please add test cases to the group before creating a launch' : testScope.total > 100 ? `A launch cannot have more than 100 test cases. Please reduce the group to 100 or fewer (currently ${testScope.total}).` : '',
    },
    {
      name: 'startType',
      type: 'radio',
      label: 'Start Time',
      options: [
        { value: 'Immediate', label: 'Immediate', disabled: false },
        { value: 'Schedule', label: 'Schedule', disabled: true }
      ],
      value: startType,
      onChange: (e) => setStartType(e.target.value),
      disabled: false,
    },
    ...(startType === 'Schedule' ? [
      {
        name: 'scheduledTime',
        type: 'date',
        label: 'Scheduled Time',
        required: true,
      },
    ] : []),
    {
      name: 'isSequential',
      type: 'checkbox',
      label: 'Run Sequentially',
      tooltip: 'Sequential mode runs tests one after another in defined order',
      onClick: (e) => setIsSequentialEnabled(Boolean(e?.target?.checked)),
    },
    ...(isSequentialEnabled ? [{
      name: 'continueOnFailure',
      type: 'checkbox',
      label: 'Continue On Failure',
    }] : []),
    {
      name: 'assignUser',
      type: 'select',
      label: 'Assign Manual Tests',
      // options: users,
      disabled: true,
    },
     {
       name: 'environment',
       type: 'select',
       label: 'Environment',
       required: false,
       options: environments.map(env => ({
         value: env.id,
         label: env.name,
       })),
       disabled: fetchingEnvironments,
       helperText: fetchingEnvironments ? 'Loading environments...' : '',
     },
    // Advanced options (conditionally rendered)
    ...(
      showAdvanced ? [
        {
          name: 'retries',
          type: 'number',
          label: 'Retries',
          disabled: true,
        },
        {
          name: 'timeout',
          type: 'number',
          label: 'Timeout (sec)',
          disabled: true,
        },
      ] : []
    ),
  ];

  // Buttons for CommonFooter
  const buttons = [
    {
      label: 'Cancel',
      variant: 'outline',
      className: 'primary',
      onClick: onClose,
      disabled: loading,
    },
    {
      label: 'Launch Now',
      variant: 'primary',
      className: 'white bg-blue600',
      onClick: handleFormSubmit,
      disabled: loading || fetchingTestScope || testScope.total === 0 || testScope.total > 100 || !canStartTestLaunch,
      loading: loading,
    },
  ];

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title="Create Test Launch"
      size="small"
      footer={
        <CommonFooter
          buttons={buttons}
          align="right"
        />
      }
    >
      {/* Advanced Options Toggle */}
      <div style={{ marginBottom: '16px' }}>
        <button
          type="button"
          onClick={() => setShowAdvanced(v => !v)}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            color: '#1976d2',
            fontSize: '14px',
            padding: '8px 0',
          }}
        >
          {showAdvanced ? <ExpandLess /> : <ExpandMore />}
          Advanced Options
        </button>
      </div>

      {modalError && <div style={{ color: 'red', marginBottom: 8 }}>{modalError}</div>}
      {!canStartTestLaunch && isTrial && (
        <div style={{ color: '#d32f2f', marginBottom: 8, fontSize: '14px' }}>
          {trialExpired ? 'Your trial has ended. Please upgrade your plan to continue.' : 'Your credits are low. Please upgrade your plan.'}
        </div>
      )}
      
      <FormWrapper
        ref={formRef}
        schema={FormSchemas.testLaunchForm(testScope)}
        fields={fields}
        onSubmit={handleConfirmSubmit}
        key={`${open ? 'open' : 'closed'}-${group?.id || 'nogroup'}`}
        defaultValues={{
          launchName: group ? getLaunchName() : '',
          startType: 'Immediate',
          isSequential: false,
          continueOnFailure: false,
          assignUser: '',
          environment: '',
          aiModel: '',
          retries: 0,
          timeout: 0,
        }}
      />
      <div style={{ marginBottom: 16 }}>
        <AIModelRuntimeSelector
          featureKey={AI_FEATURE_KEYS.TEST_LAUNCH}
          trialOrgSkip={isTrial}
          projectId={projectId ?? group?.project_id}
          required={(testScope.automated || 0) > 0}
          disabled={fetchingTestScope || (testScope.automated || 0) === 0 || !canSelectAiModel || isTrial}
          readOnly={!canSelectAiModel && !isTrial}
          label="AI Model"
          helperText={
            fetchingTestScope
              ? 'Loading test scope...'
              : (testScope.automated || 0) === 0
                ? 'No NLP automated tests in this group'
                : undefined
          }
          onModelChange={(model) => {
            const v =
              model && model.provider && model.model_key
                ? JSON.stringify({ provider: model.provider, model_key: model.model_key })
                : '';
            formRef.current?.setValue?.('aiModel', v, { shouldValidate: true });
          }}
        />
      </div>
    </ModalWrapper>
  );
}

export default CreateTestLaunchModal;